// src/services/e2eeVerify.js — the security code, and its SYNCED verification state.
//
// ── THE CODE ────────────────────────────────────────────────────────────────
// Byte-for-byte the app's algorithm (ChatInfoScreen.generateKeyFingerprint), so
// the 60 digits shown here are the same 60 digits the phones show — which is the
// whole point: a code that differed by platform would be worthless.
//
//   1. collect every participant's per-device public keys that can still read
//      (users/{uid}.devices[*].publicKey, minus logged-out and revoked devices —
//      sealDevices.departed; never the legacy users/{uid}.publicKey)
//   2. sort the flat list of keys, join with no separator
//   3. SHA-512 it with nacl.hash
//   4. render each of the 64 bytes as 3 digits ("000".."255"), take the first 60
//   5. group in blocks of 5
//
// ── THE SYNC ────────────────────────────────────────────────────────────────
// The app keeps its trust-on-first-use baseline in AsyncStorage, which is
// per-device by definition: verify on the phone and the web knows nothing, and
// the other person never learns you verified at all. So the shared state lives
// on the chat doc, where every device of every participant already listens:
//
//   chats/{id}.e2ee = {
//     baseline: { [uid]: code },                 // what THIS account last accepted
//     verified: { [uid]: { code, at, via } },    // a human confirmed it (QR / read-aloud)
//   }
//
// Reading someone else's entry is how "they verified this too" appears on both
// sides. Writing only ever touches your own uid's slot.

import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import { db } from '../config/firebase';
import { departed } from './sealDevices';

/** The app's exact fingerprint: SHA-512 → 3-digit bytes → 60 digits → blocks of 5. */
export function fingerprintOf(combinedKeys) {
  const messageUint8 = util.decodeUTF8(combinedKeys);
  const hashArray = nacl.hash(messageUint8);
  let numericString = '';
  for (let i = 0; i < hashArray.length; i += 1) {
    numericString += hashArray[i].toString().padStart(3, '0');
  }
  const code60 = numericString.substring(0, 60);
  return code60.match(/.{1,5}/g)?.join(' ') || code60;
}

/** Every public key that actually seals messages for this user. */
async function keysForUser(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.data() || {};
  const devices = data.devices || {};
  /* 🔐 THE KEYS THAT CAN READ NEW MESSAGES — the phone's exact rule (services/keyChange
     activeKeys): a logged-out or revoked device is not in the code, the account-wide legacy key
     never is. Decided from the entries alone, so every device shows the same 60 digits. */
  const out = new Set();
  for (const dId of Object.keys(devices)) {
    const d = devices[dId];
    if (!d || typeof d.publicKey !== 'string' || !d.publicKey || departed(d)) continue;
    out.add(String(d.publicKey));
  }
  if (out.size === 0) throw new Error(`missing key for ${uid}`);
  return Array.from(out);
}

/** The 60-digit code for this conversation. `uids` = every participant, me included. */
export async function securityCodeFor(uids) {
  let keys = [];
  for (const uid of uids) {
    // eslint-disable-next-line no-await-in-loop
    keys = keys.concat(await keysForUser(uid));
  }
  keys.sort();
  return fingerprintOf(keys.join(''));
}

/* ── the shared state on the chat doc ─────────────────────────────────────── */

export function watchE2ee(chatId, cb) {
  if (!chatId) { cb(null); return () => {}; }
  return onSnapshot(doc(db, 'chats', chatId), (s) => cb(s.data()?.e2ee || {}), () => cb({}));
}

/** Accept today's code as this account's baseline (trust on first use, or after
 *  the human compared it). Only ever writes my own slot. */
export function setBaseline(chatId, uid, code) {
  return updateDoc(doc(db, 'chats', chatId), { [`e2ee.baseline.${uid}`]: code });
}

/** A human confirmed the code — record it where BOTH sides can see it. */
export function markVerified(chatId, uid, code, via = 'web') {
  return updateDoc(doc(db, 'chats', chatId), {
    [`e2ee.baseline.${uid}`]: code,
    [`e2ee.verified.${uid}`]: { code, at: Date.now(), via },
  });
}

/** Drop my verification (used when I choose to re-verify from scratch). */
export function clearVerified(chatId, uid) {
  return updateDoc(doc(db, 'chats', chatId), {
    [`e2ee.verified.${uid}`]: null,
  });
}

/**
 * What the UI should say, given today's code and the synced state.
 *   'changed'  my accepted baseline no longer matches today's keys → warn loudly
 *   'verified' a human on MY account confirmed this exact code
 *   'peer'     they verified it, I haven't
 *   'ok'       encrypted, first look (baseline just taken)
 */
export function verdictOf(e2ee, code, myUid, peerUids) {
  const baseline = e2ee?.baseline?.[myUid] || null;
  const mine = e2ee?.verified?.[myUid] || null;
  const peers = (peerUids || [])
    .map((u) => ({ uid: u, v: e2ee?.verified?.[u] || null }))
    .filter((x) => x.v && x.v.code === code);
  if (baseline && baseline !== code) return { state: 'changed', mine, peers };
  if (mine && mine.code === code) return { state: 'verified', mine, peers };
  if (peers.length) return { state: 'peer', mine: null, peers };
  return { state: 'ok', mine: null, peers };
}
